/** Pricing: copy deck §6. Three tiers gated on mailboxes + monthly sends,
 * with per-lead AI personalization as a metered add-on. Rendered as clean white
 * PhishFence-style comparison cards; Growth is the popular tier with an emerald
 * ring + ribbon. Prices are PROPOSED placeholders per the deck. */
import { Section, SectionHeading, StartFreeButton } from './Section.js';
import { clsx } from 'clsx';

interface Tier {
  name: string;
  price: string;
  bestFor: string;
  popular?: boolean;
  rows: { label: string; value: string }[];
}

const TIERS: Tier[] = [
  {
    name: 'Solo',
    price: '$29',
    bestFor: 'One founder, one domain',
    rows: [
      { label: 'Connected mailboxes', value: 'Up to 2' },
      { label: 'Monthly sends', value: '5,000' },
      { label: 'Warmup', value: 'Included' },
      { label: 'Caps, ramp, auto-pause', value: 'Included' },
      { label: 'Deliverability + auth checks', value: 'Included' },
      { label: 'Unlimited contacts & sequences', value: '✓' },
      { label: 'AI sequence drafting', value: '✓' },
      { label: 'Per-lead AI personalization', value: 'Add-on' },
      { label: 'Client workspaces', value: '-' },
    ],
  },
  {
    name: 'Growth',
    price: '$79',
    bestFor: 'Small team scaling outbound',
    popular: true,
    rows: [
      { label: 'Connected mailboxes', value: 'Up to 10' },
      { label: 'Monthly sends', value: '25,000' },
      { label: 'Warmup', value: 'Included' },
      { label: 'Caps, ramp, auto-pause', value: 'Included' },
      { label: 'Deliverability + auth checks', value: 'Included' },
      { label: 'Unlimited contacts & sequences', value: '✓' },
      { label: 'AI sequence drafting', value: '✓' },
      { label: 'Per-lead AI personalization', value: 'Add-on' },
      { label: 'Client workspaces', value: '-' },
    ],
  },
  {
    name: 'Agency',
    price: '$199',
    bestFor: 'Agencies running client outreach',
    rows: [
      { label: 'Connected mailboxes', value: 'Up to 30' },
      { label: 'Monthly sends', value: '100,000' },
      { label: 'Warmup', value: 'Included' },
      { label: 'Caps, ramp, auto-pause', value: 'Included' },
      { label: 'Deliverability + auth checks', value: 'Included' },
      { label: 'Unlimited contacts & sequences', value: '✓' },
      { label: 'AI sequence drafting', value: '✓' },
      { label: 'Per-lead AI personalization', value: 'Add-on' },
      { label: 'Client workspaces', value: '✓' },
    ],
  },
];

export function Pricing() {
  return (
    <Section id="pricing" className="bg-white">
      <SectionHeading
        align="center"
        eyebrow="Pricing"
        title="Simple pricing. Priced to keep you safe, not to push volume."
        lead={
          <>
            Plans scale with mailboxes and sends, the things that actually affect
            deliverability. AI personalization is pay-as-you-go, so you&rsquo;re
            never paying for tokens you don&rsquo;t use.
          </>
        }
      />

      <div className="mt-12 grid items-stretch gap-6 lg:grid-cols-3">
        {TIERS.map((tier) => (
          <div
            key={tier.name}
            className={clsx(
              'card relative flex flex-col p-7',
              tier.popular && 'ring-2 ring-brand-600',
            )}
          >
            {tier.popular && (
              <span className="absolute -top-3 left-7 rounded-full bg-brand-700 px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-white shadow-sm">
                Most popular
              </span>
            )}
            <span className="font-mono text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              {tier.name}
            </span>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="font-display text-4xl font-bold tracking-tight text-slate-900">
                {tier.price}
              </span>
              <span className="text-slate-500">/mo</span>
            </div>
            <div className="mt-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              Proposed pricing
            </div>
            <p className="mt-3 text-sm text-slate-600">{tier.bestFor}</p>

            <dl className="mt-6 space-y-2.5 text-sm">
              {tier.rows.map((row) => (
                <div
                  key={row.label}
                  className="flex items-baseline justify-between gap-4 border-b border-slate-100 pb-2.5"
                >
                  <dt className="text-slate-600">{row.label}</dt>
                  <dd
                    className={clsx(
                      'font-mono text-[13px] font-medium',
                      row.value === '-' ? 'text-slate-400' : 'text-slate-900',
                    )}
                  >
                    {row.value}
                  </dd>
                </div>
              ))}
            </dl>

            <div className="mt-7 pt-1">
              <StartFreeButton className="w-full" />
            </div>
          </div>
        ))}
      </div>

      <div className="card mt-10 bg-slate-50 p-6 text-center">
        <p className="mx-auto max-w-3xl leading-relaxed text-slate-600">
          <span className="font-semibold text-slate-900">
            Per-lead AI personalization
          </span>: metered add-on, pay only for what you generate.{' '}
          <span className="text-slate-400">
            (Proposed: ~$X per 1,000 personalized lines, founder to set against
            actual model cost.)
          </span>
        </p>
        <p className="mt-3 font-mono text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          Annual billing: proposed 2 months free
        </p>
      </div>
      <p className="mt-6 text-center text-slate-600">
        No long contracts. Connect a mailbox, send your first safe campaign,
        decide later.
      </p>
    </Section>
  );
}
